import { Download, ArrowLeft, Mail, ShieldCheck, Share2, LineChart, Users, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import PortalLayout from "../../components/PortalLayout";

/**
 * Partner Playbook — online, single-source-of-truth of the printable PDF.
 *
 * English-only by design. The equivalent branded PDF is served by
 * `GET /api/introducer/playbook` and can be downloaded from the CTA at the
 * top of this page.
 */
export default function IntroducerGuidePage() {
  const BACKEND_URL =
    typeof window !== "undefined" && window.location && window.location.origin
      ? window.location.origin
      : (process.env.REACT_APP_BACKEND_URL || "");

  return (
    <PortalLayout>
      <section
        className="max-w-[860px] mx-auto px-6 sm:px-8 pt-14 pb-16"
        data-testid="introducer-guide-page"
      >
        <Link
          to="/introducer"
          className="inline-flex items-center gap-2 br-overline mb-6 hover:text-[color:var(--br-parchment)] transition-colors"
          data-testid="guide-back"
        >
          <ArrowLeft size={14} /> Introducer dashboard
        </Link>

        <div className="br-fade-up">
          <div className="br-overline">Partner playbook</div>
          <h1 className="font-serif-display text-[clamp(2.4rem,4.5vw,3.6rem)] mt-3 leading-[1.05]">
            A step-by-step <em>guide</em> for Introducer Partners.
          </h1>
          <p className="text-[1.05rem] text-[color:var(--br-text)] mt-4 leading-relaxed">
            Everything you need to know: how your account works, how to
            introduce clients, what happens after they arrive, and how you are
            paid. Written in plain language.
          </p>
        </div>

        <div className="mt-8 br-card br-aurora-ribbon px-8 py-6 flex flex-wrap justify-between items-center gap-4" data-testid="playbook-download-cta">
          <div>
            <div className="br-overline">Printable version</div>
            <p className="mt-2 text-[0.95rem] text-[color:var(--br-text)]">
              A branded, illustrated PDF you can share offline or hand out at a
              meeting.
            </p>
          </div>
          <a
            href={`${BACKEND_URL}/api/introducer/playbook`}
            target="_blank"
            rel="noreferrer"
            className="br-btn-primary inline-flex items-center gap-2"
            style={{ padding: "12px 22px" }}
            data-testid="download-playbook-pdf"
          >
            <Download size={16} /> Download PDF
          </a>
        </div>

        {/* Table of contents */}
        <nav className="mt-10 br-card px-8 py-6" aria-label="Contents" data-testid="playbook-toc">
          <div className="br-overline mb-4">Contents</div>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 list-decimal list-inside text-[0.95rem] text-[color:var(--br-text)]">
            <li><a href="#ch-1" className="hover:text-[color:var(--br-bronze-soft)]">Your Partner Account</a></li>
            <li><a href="#ch-2" className="hover:text-[color:var(--br-bronze-soft)]">Introducing Clients</a></li>
            <li><a href="#ch-3" className="hover:text-[color:var(--br-bronze-soft)]">Partner Mode</a></li>
            <li><a href="#ch-4" className="hover:text-[color:var(--br-bronze-soft)]">Commissions &amp; Reporting</a></li>
            <li><a href="#ch-5" className="hover:text-[color:var(--br-bronze-soft)]">Confidentiality &amp; Compliance</a></li>
            <li><a href="#ch-6" className="hover:text-[color:var(--br-bronze-soft)]">Support &amp; Escalation</a></li>
            <li><a href="#appendix-a" className="hover:text-[color:var(--br-bronze-soft)]">Quick Reference</a></li>
            <li><a href="#appendix-b" className="hover:text-[color:var(--br-bronze-soft)]">Frequently Asked Questions</a></li>
          </ol>
        </nav>

        {/* Chapter 1 */}
        <Chapter
          id="ch-1"
          number="Chapter 1"
          title="Your Partner Account"
          Icon={ShieldCheck}
        >
          <Section title="1.1 How your account is created">
            <P>
              Your Partner account is created by the Borealis Reserve back
              office as soon as your onboarding paperwork is complete. The
              moment your record is saved:
            </P>
            <Bullets items={[
              <><Strong>A unique promo code</Strong> is generated for you (for example <Strong>BR-ROSSI-2026</Strong>). This code is yours forever and is used to attribute every client and every commission to you across all Borealis systems.</>,
              <><Strong>A draft partnership contract</Strong> is prepared as a PDF and emailed to you from <Strong>concierge@borealisreserve.com</Strong>.</>,
              <><Strong>An invitation email</Strong> with a personal, single-use activation link is sent to the address you registered with. The link is valid for seven days.</>,
            ]} />
          </Section>

          <Section title="1.2 First login">
            <P>
              Open the activation link from your email and choose a password.
              From that moment on you log in at <Strong>https://borealisreserve.com/login</Strong>.
              Passwords must be at least ten characters and contain letters,
              numbers and a symbol.
            </P>
            <P>
              If you forget your password, use the <em>Forgot password?</em>
              {" "}link on the login screen. You will receive a secure recovery
              link or a temporary password, depending on the option chosen by
              our team.
            </P>
            <P>
              If you want to add an extra layer of protection you can enable
              two-factor authentication from the <Strong>Security</Strong> area
              of your portal. We strongly recommend it, especially if you work
              from multiple devices.
            </P>
          </Section>

          <Section title="1.3 Signing your partnership contract">
            <P>
              The first thing you will see on your dashboard, before you can
              start introducing clients, is a red banner asking you to
              <Strong> review and sign</Strong> the partnership contract.
              Signing is fully digital: you click on the banner, read the PDF
              inside the viewer, tick the acknowledgment checkbox, and press
              {" "}<em>Sign digitally</em>. The signature records:
            </P>
            <Bullets items={[
              "the exact date and time (UTC),",
              "the IP address from which you signed,",
              "your browser fingerprint,",
              "a cryptographic hash of the document you signed, so its integrity can be verified years later.",
            ]} />
            <P>
              A <Strong>countersigned PDF</Strong> is then emailed back to you
              and always available in the <Strong>Contract</Strong> area of
              your portal.
            </P>
            <Callout>
              Until the contract is signed, monthly statements cannot be
              published on your account and referred clients are held in a
              temporary review area. Please sign it as soon as possible.
            </Callout>
          </Section>

          <Section title="1.4 Your dashboard at a glance">
            <P>
              Once the contract is signed, your Partner dashboard becomes your
              daily home. It looks like this:
            </P>
            <Figure
              src="/playbook-assets/01-dashboard.png"
              caption="Figure 1 — Partner dashboard: contract banner, KPIs, share link and monthly-statements shortcut."
            />
            <P>Four summary tiles at the top show:</P>
            <Bullets items={[
              <><Strong>Total referrals</Strong> — how many people you have introduced, and how many are active.</>,
              <><Strong>AUM introduced</Strong> — the total capital they have deposited.</>,
              <><Strong>Accrued (net)</Strong> — the commission you have earned so far, net of any applicable withholding.</>,
              <><Strong>Yearly estimate (net)</Strong> — a projection of the commission you would earn in a full year if today's book stayed exactly the same.</>,
            ]} />
          </Section>
        </Chapter>

        {/* Chapter 2 */}
        <Chapter
          id="ch-2"
          number="Chapter 2"
          title="Introducing Clients"
          Icon={Share2}
        >
          <Section title="2.1 Your promo code and referral link">
            <P>
              Every Partner has a personal referral link that already carries
              their promo code. Anyone who reaches Borealis Reserve through
              this link is automatically attributed to you.
            </P>
            <Figure
              src="/playbook-assets/02-share-link-qr.png"
              caption="Figure 2 — Share card: your unique referral URL, a copy button and a printable QR code."
            />
            <P>You have three ways to share it:</P>
            <Bullets items={[
              <><Strong>Copy the link</Strong> and paste it into an email, a chat message or your signature. One click on the <em>Copy link</em> button places the URL on your clipboard.</>,
              <><Strong>Show the QR code</Strong> in person — for example on a tablet during a client meeting, on a business card, or on a printed leaflet. When your contact scans it, they arrive on the Borealis landing page already associated with your code.</>,
              <><Strong>Give them your code directly</Strong>, for example over the phone. They can type <Strong>BR-XXXX-2026</Strong> into the promo code field on the Borealis home page.</>,
            ]} />
            <P>
              All three routes lead to the same outcome: the contact is stored
              on our side with the note <em>"introduced by you"</em>, and every
              future step of their journey is tracked against your account.
            </P>
          </Section>

          <Section title="2.2 What the client sees when they arrive">
            <P>
              The referral link opens a <Strong>welcome page</Strong>,
              personalised in the visitor's own language whenever possible
              (Italian, English, Norwegian or German). It briefly explains
              what Borealis Reserve is, invites the visitor to request an
              introduction, and collects basic contact details.
            </P>
            <P>
              The visitor never has to install anything and never sees pricing
              or products at this stage. Our club is invitation-only, and the
              first conversation is always with a member of the Borealis team.
            </P>
          </Section>

          <Section title="2.3 What happens next">
            <Bullets items={[
              <><Strong>Borealis receives the lead</Strong>, tagged with your promo code and the language the visitor was using.</>,
              <>A <Strong>Relationship Manager</Strong> contacts the lead within one business day (Central European time) to introduce the club and confirm interest.</>,
              <>If the contact wants to proceed, they receive a <Strong>personal invitation email</Strong> that lets them create a private account and start the identity-verification process (KYC).</>,
              <>Once identity-verified, the client can subscribe to one of our plans.</>,
              <>Every step is reflected in your Referrals list, in aggregate form.</>,
            ]} />
          </Section>

          <Section title="2.4 A quick note on KYC">
            <P>
              <em>KYC</em> stands for "Know Your Customer" and is the
              identity-verification process required by European regulation
              before anyone can invest with us. A new client uploads a proof
              of identity, a proof of address, and answers a short
              questionnaire about their financial background.
            </P>
            <P>
              You never have to help the client with KYC — the Borealis
              Compliance team guides them directly. Your only visibility is a
              status bar on the Referrals page.
            </P>
          </Section>
        </Chapter>

        {/* Chapter 3 */}
        <Chapter
          id="ch-3"
          number="Chapter 3"
          title="Partner Mode"
          Icon={Users}
        >
          <P>
            Your Partner portal gives you a <Strong>read-only view</Strong> of
            everything a member of the club can see. Partner Mode is designed
            so that you can explain the product accurately in front of a
            prospect, without having any purchasing power yourself.
          </P>

          <Section title="3.1 The yield simulator">
            <P>
              Under <Strong>Plans</Strong> you can move a slider between
              different capital brackets and show your prospect an indicative
              projection of how a plan would perform over time. The simulator
              shows the gross expected yield, the net yield after fees, monthly
              and yearly projected earnings, and a small chart of the capital
              curve over the plan duration.
            </P>
          </Section>

          <Section title="3.2 The plan catalogue">
            <P>
              The full plan catalogue is available as a browsable list, complete
              with name, tier and duration; asset-allocation breakdown; volatility
              band; and a link to the <Strong>KID</Strong> (Key Information
              Document) of each plan.
            </P>
          </Section>

          <Section title="3.3 The events calendar">
            <P>
              You can also see the <Strong>club events calendar</Strong>:
              private dinners, macro briefings, round tables and online
              conferences. In Partner Mode you cannot RSVP on behalf of a
              client, but you can preview locations and dates so you can
              mention upcoming events when it makes sense.
            </P>
          </Section>

          <Section title="3.4 What you will not see in Partner Mode">
            <P>
              To keep the boundary between Partner and Client roles clean,
              three actions are deliberately hidden from your view:
            </P>
            <Bullets items={[
              <>the <Strong>Subscribe</Strong> button on any plan,</>,
              <>the <Strong>RSVP</Strong> button on any event,</>,
              <>any <Strong>personal deposit or statement</Strong> of a client.</>,
            ]} />
            <P>
              If you ever want to invest in a Borealis product yourself, you
              must open a separate client account.
            </P>
          </Section>
        </Chapter>

        {/* Chapter 4 */}
        <Chapter
          id="ch-4"
          number="Chapter 4"
          title="Commissions and Reporting"
          Icon={LineChart}
        >
          <Section title="4.1 How commissions accrue">
            <P>
              Commissions accrue <Strong>daily and linearly</Strong> on the
              capital your referred clients have invested and that is still
              active. There is no compounding, no ramp-up, no cliff: from day
              one after a deposit becomes active, the meter starts running in
              your favour.
            </P>
            <P>
              The commission rate and any applicable withholding are set in
              your personal contract — please refer to your signed PDF for the
              exact figures.
            </P>
            <Bullets items={[
              <><Strong>Client fees do not reduce your commission.</Strong> Management and performance fees Borealis charges the client are computed separately from what we pay you.</>,
              <><Strong>Client capital movements are handled cleanly.</Strong> If a client closes a deposit early, your daily accrual stops the same day. If a client tops up, the increment starts counting immediately.</>,
            ]} />
          </Section>

          <Section title="4.2 Monthly statements">
            <P>
              At the end of every calendar month, the Borealis Finance team
              publishes your <Strong>monthly commission statement</Strong> as
              a PDF. You will find it in the <Strong>Monthly statements</Strong>
              {" "}area of your portal:
            </P>
            <Figure
              src="/playbook-assets/03-statements.png"
              caption="Figure 3 — Monthly statements page: period label, statement number, gross/withholding/net columns and download link."
            />
            <P>Every statement contains:</P>
            <Bullets items={[
              "the period label and a unique statement number,",
              "the gross commission earned in the period,",
              "any withholding tax deducted at source,",
              "the net amount paid to your bank account.",
            ]} />
            <P>
              The PDF is <Strong>countersigned by Borealis</Strong> and
              archived indefinitely for tax purposes.
            </P>
          </Section>

          <Section title="4.3 Payment cadence">
            <Bullets items={[
              "The statement is published within the first ten business days of the month that follows.",
              "A copy of the PDF lands in your inbox as soon as it is published.",
              "The net amount is wired to the bank account we hold on file for you, typically within seven business days from publication.",
            ]} />
            <P>
              If the bank details we hold need to change, please email
              {" "}<Strong>finance@borealisreserve.com</Strong> before the end
              of the month — we cannot amend a statement that has already been
              paid out.
            </P>
          </Section>

          <Section title="4.4 Contract renewals">
            <P>
              Your partnership contract renews on a fixed schedule described
              in the contract itself. Sixty days before renewal, the system
              sends you an automated reminder from <Strong>concierge@borealisreserve.com</Strong>.
              You do not need to do anything unless you want to change the
              terms.
            </P>
          </Section>
        </Chapter>

        {/* Chapter 5 */}
        <Chapter
          id="ch-5"
          number="Chapter 5"
          title="Confidentiality and Compliance"
          Icon={ShieldCheck}
        >
          <Section title="5.1 Why your visibility is deliberately limited">
            <P>
              Borealis Reserve serves European clients under
              <Strong> MiFID II</Strong> and <Strong>GDPR</Strong> frameworks.
              Both regulations require us to protect our clients' identity and
              financial information from third parties, including partners who
              introduced them. This is why your Referrals list shows counts,
              capital and commissions but never names, emails, phone numbers
              or KYC documents.
            </P>
            <Bullets items={[
              "You will never see who has completed KYC by name.",
              "You will never see how much a specific client has deposited.",
              "You will never see a client's contact details or documents.",
              "All commercial follow-ups happen through the Borealis Relationship Manager.",
            ]} />
            <P>
              If a client contacts you directly and wants to talk, you are of
              course free to reply — you already knew them before they became
              a Borealis client.
            </P>
          </Section>

          <Section title="5.2 What you can and cannot say to prospects">
            <Bullets items={[
              <><Strong>Always describe expected yields as expected, not guaranteed.</Strong> Even our plan pages use the phrase "expected gross yield". There are no guaranteed returns.</>,
              <><Strong>Always mention that capital is at risk.</Strong> The value of investments can fall as well as rise.</>,
              <><Strong>Never provide investment advice.</Strong> You introduce contacts to us; the suitability assessment and the actual advice are performed by the Borealis team.</>,
              <><Strong>Communications are marketing, not offers.</Strong> A conversation with a prospect is marketing under EU rules. It does not constitute a public offer of financial products.</>,
            ]} />
            <P>
              These three statements appear as a fixed disclaimer at the
              bottom of every page in your portal, and must appear in any
              material you distribute in Borealis' name. If in doubt, check
              with <Strong>compliance@borealisreserve.com</Strong>.
            </P>
          </Section>

          <Section title="5.3 If you are contacted by a regulator">
            <P>
              In the unlikely event that a regulatory authority (Consob,
              Finanstilsynet, BaFin, FCA, etc.) contacts you about a client
              you introduced, please:
            </P>
            <Bullets items={[
              <>Do <Strong>not</Strong> attempt to answer factual questions yourself.</>,
              <>Acknowledge receipt of the request in writing.</>,
              <>Forward the request immediately to <Strong>legal@borealisreserve.com</Strong> and <Strong>compliance@borealisreserve.com</Strong>.</>,
            ]} />
          </Section>
        </Chapter>

        {/* Chapter 6 */}
        <Chapter
          id="ch-6"
          number="Chapter 6"
          title="Support and Escalation"
          Icon={Mail}
        >
          <Section title="6.1 Where to write">
            <ContactTable rows={[
              ["General questions, portal access", "concierge@borealisreserve.com"],
              ["A specific referred client", "clients@borealisreserve.com"],
              ["KYC or a compliance matter", "compliance@borealisreserve.com"],
              ["Commission statements, bank details", "finance@borealisreserve.com"],
              ["Contractual or legal questions", "legal@borealisreserve.com"],
            ]} />
          </Section>

          <Section title="6.2 In case of a portal outage">
            <Bullets items={[
              "Wait five minutes and try again (some transient hiccups auto-resolve).",
              "Check whether the issue is on your side by opening the URL from a different network (for example, from a mobile hotspot).",
              <>If the problem persists, write to <Strong>concierge@borealisreserve.com</Strong>. We will confirm the incident and give you an expected recovery time.</>,
            ]} />
          </Section>

          <Section title="6.3 Security incidents">
            <P>
              If you suspect your Partner account has been accessed by someone
              else, please act immediately:
            </P>
            <Bullets items={[
              <>Change your password from the <Strong>Security</Strong> area.</>,
              "Enable two-factor authentication if it is not yet on.",
              <>Email <Strong>compliance@borealisreserve.com</Strong> with the date and time of any suspicious activity.</>,
            ]} />
          </Section>
        </Chapter>

        {/* Appendix A */}
        <Chapter
          id="appendix-a"
          number="Appendix A"
          title="Quick Reference"
          Icon={FileText}
        >
          <ContactTable rows={[
            ["My portal", "https://borealisreserve.com"],
            ["My promo code", "BR-XXXX-YYYY (top-right of your dashboard)"],
            ["My referral link", "https://borealisreserve.com/referral/BR-XXXX-YYYY"],
            ["Concierge", "concierge@borealisreserve.com"],
            ["Finance", "finance@borealisreserve.com"],
            ["Compliance", "compliance@borealisreserve.com"],
          ]} />
        </Chapter>

        {/* Appendix B */}
        <Chapter
          id="appendix-b"
          number="Appendix B"
          title="Frequently Asked Questions"
          Icon={FileText}
        >
          <Faq q="Can I introduce a member of my own family?">
            Yes, provided they meet the club's eligibility and complete KYC
            like any other client. The commission mechanics are the same.
          </Faq>
          <Faq q="Can I introduce a company or a foundation?">
            Yes — Borealis accepts both natural persons and legal entities.
            The client's KYC will be adapted to the entity type (UBO check,
            corporate documents, etc.) and handled by our Compliance team.
          </Faq>
          <Faq q="What happens to my commissions if a client leaves the club?">
            Commissions stop accruing on the day the client's last deposit
            closes. Any commission that has already accrued up to that day
            remains payable to you and appears in the next monthly statement.
          </Faq>
          <Faq q="Can I share my portal login with an assistant?">
            No — every login is nominal and audited. If you need a second
            person to access statements on your behalf, please write to
            {" "}<Strong>concierge@borealisreserve.com</Strong> and we will
            create a separate secondary account with limited permissions.
          </Faq>
          <Faq q="In what language will I receive emails and PDFs?">
            The language you selected at first login (Italian, English,
            Norwegian or German). You can change it any time from the language
            switcher in the top navigation.
          </Faq>
          <Faq q="Do I need special software to open the statement PDFs?">
            No — any modern PDF reader works, on any device. The files are
            not password protected.
          </Faq>
        </Chapter>

        <p className="mt-16 text-[0.78rem] text-[color:var(--br-text-dim)] italic text-center">
          Borealis Reserve — Members only. Participation terms and yields shown
          in portal materials do not constitute a public offering or an
          investment solicitation under applicable regulations.
        </p>
      </section>
    </PortalLayout>
  );
}

/* ---------- Small building blocks ---------- */

function Chapter({ id, number, title, Icon, children }) {
  return (
    <article id={id} className="mt-16 scroll-mt-24" data-testid={`chapter-${id}`}>
      <header className="border-t pt-8" style={{ borderColor: "var(--br-line)" }}>
        <div className="br-overline inline-flex items-center gap-2">
          {Icon ? <Icon size={12} /> : null}
          {number}
        </div>
        <h2 className="font-serif-display text-[2rem] mt-2 leading-tight text-[color:var(--br-parchment)]">
          {title}
        </h2>
      </header>
      <div className="mt-6 space-y-5">{children}</div>
    </article>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <h3 className="font-mono-fin text-[0.7rem] tracking-[0.24em] uppercase text-[color:var(--br-bronze)] mt-6">
        {title}
      </h3>
      {children}
    </div>
  );
}

function P({ children }) {
  return (
    <p className="text-[0.95rem] leading-[1.7] text-[color:var(--br-text)]">
      {children}
    </p>
  );
}

function Strong({ children }) {
  return <strong className="text-[color:var(--br-parchment)] font-semibold">{children}</strong>;
}

function Bullets({ items }) {
  return (
    <ul className="space-y-2 pl-5">
      {items.map((item, i) => (
        <li
          key={`bullet-${i}`}
          className="text-[0.95rem] leading-[1.7] text-[color:var(--br-text)] relative before:content-['■'] before:absolute before:-left-4 before:top-0 before:text-[color:var(--br-bronze)] before:text-[0.5em] before:translate-y-[6px]"
        >
          {item}
        </li>
      ))}
    </ul>
  );
}

function Figure({ src, caption }) {
  return (
    <figure className="my-6">
      <div className="br-card overflow-hidden" style={{ padding: 6, background: "#0e1422" }}>
        <img src={src} alt={caption} className="w-full h-auto block" loading="lazy" />
      </div>
      <figcaption className="mt-3 text-[0.78rem] italic text-[color:var(--br-text-dim)] text-center">
        {caption}
      </figcaption>
    </figure>
  );
}

function Callout({ children }) {
  return (
    <div
      className="mt-2 px-6 py-4 text-[0.9rem] leading-[1.6] italic text-[color:var(--br-parchment)]"
      style={{
        borderLeft: "2.5px solid var(--br-bronze-soft)",
        background: "rgba(199,154,88,0.06)",
      }}
    >
      {children}
    </div>
  );
}

function ContactTable({ rows }) {
  return (
    <table className="w-full text-left border-collapse text-[0.9rem]">
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label} className="border-b" style={{ borderColor: "var(--br-line)" }}>
            <th
              scope="row"
              className="py-3 pr-4 font-mono-fin text-[0.62rem] tracking-[0.18em] uppercase font-medium align-top w-[45%]"
              style={{ color: "var(--br-bronze)" }}
            >
              {label}
            </th>
            <td className="py-3 text-[color:var(--br-parchment)] font-mono-fin text-[0.85rem]">
              {value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Faq({ q, children }) {
  return (
    <div className="mt-5">
      <div className="font-serif-display italic text-[1.1rem] text-[color:var(--br-parchment)]">
        Q. {q}
      </div>
      <P>{children}</P>
    </div>
  );
}
